import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/payrollView";
import { Lock, Trash2, AlertTriangle, Pencil, Download, History } from "lucide-react";

const EMPTY = { tech_profile_id: "", amount: "", reason: "", source_week_monday: "" };

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (d?.blocking_condition === "RUN_FROZEN") return `Run is frozen (${d.reason}). Corrections are read-only.`;
  return typeof d === "string" ? d : d ? JSON.stringify(d) : fallback;
};

const validate = (f) => {
  if (!f.tech_profile_id) return "Pick a tech.";
  const n = Number(f.amount);
  if (f.amount === "" || Number.isNaN(n) || n === 0) return "Amount must be a non-zero number (use − for a deduction).";
  if ((f.reason || "").trim().length < 10) return "Reason must be at least 10 characters.";
  return null;
};

function CorrectionForm({ value, onChange, roster, testPrefix }) {
  return (
    <>
      <select value={value.tech_profile_id} onChange={(e) => onChange({ ...value, tech_profile_id: e.target.value })}
              className="border rounded px-2 py-1" data-testid={`${testPrefix}-tech`}>
        <option value="">Tech…</option>
        {roster.map((t) => <option key={t.person_id} value={t.person_id}>{t.name}</option>)}
      </select>
      <input type="number" step="0.01" placeholder="Amount ±" value={value.amount}
             onChange={(e) => onChange({ ...value, amount: e.target.value })}
             className="border rounded px-2 py-1 w-28" data-testid={`${testPrefix}-amount`} />
      <input placeholder="Reason (10+ chars)" value={value.reason}
             onChange={(e) => onChange({ ...value, reason: e.target.value })}
             className="border rounded px-2 py-1 flex-1 min-w-[200px]" data-testid={`${testPrefix}-reason`} />
      <input type="date" value={value.source_week_monday || ""}
             onChange={(e) => onChange({ ...value, source_week_monday: e.target.value })}
             className="border rounded px-2 py-1" data-testid={`${testPrefix}-week`} title="Source week (Monday)" />
    </>
  );
}

// CC-067 Phase 1 v5b: per-run corrections. Real runs only; loads from the API on mount (survives reload).
export function CorrectionsPanel({ runId, slug, paylines, frozen, canEdit, onRecompute }) {
  const [rows, setRows] = useState([]);
  const [roster, setRoster] = useState([]);
  const [audit, setAudit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const q = `?tenant_slug=${slug}`;
  const editable = canEdit && !frozen;

  const load = useCallback(async () => {
    try { setRows((await api.get(`/payroll/runs/${runId}/corrections${q}`)).data || []); }
    catch (e) { setErr(errText(e, "Could not load corrections")); }
  }, [runId, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canEdit) return;
    api.get(`/payroll/roster${q}`).then(({ data }) => setRoster(data?.rows || [])).catch(() => setRoster([]));
  }, [canEdit, q]);

  const act = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); setSaved(true); await load(); if (audit) await loadAudit(); return true; }
    catch (e) { setErr(errText(e, "Save failed")); return false; }
    finally { setBusy(false); }
  };
  const loadAudit = async () => {
    try { setAudit((await api.get(`/payroll/runs/${runId}/corrections/audit${q}`)).data || []); }
    catch (e) { setErr(errText(e, "Could not load history")); }
  };
  const add = async () => {
    const v = validate(form); if (v) { setErr(v); return; }
    if (await act(() => api.post(`/payroll/runs/${runId}/corrections${q}`, { ...form, amount: Number(form.amount) }))) setForm(EMPTY);
  };
  const saveEdit = async () => {
    const v = validate(edit); if (v) { setErr(v); return; }
    if (await act(() => api.patch(`/payroll/runs/${runId}/corrections/${edit.id}${q}`, { ...edit, amount: Number(edit.amount) }))) setEdit(null);
  };
  const remove = (id) => {
    if (!window.confirm("Delete this correction?")) return;
    act(() => api.delete(`/payroll/runs/${runId}/corrections/${id}${q}`));
  };

  const exportCsv = async () => {
    setErr(null);
    try {
      const res = await api.get(`/payroll/runs/${runId}/payout-export.csv${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payout-export-${runId.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try { e.response.data = JSON.parse(await blob.text()); } catch { /* not JSON */ }
      }
      setErr(errText(e, "Export failed"));
    }
  };

  const paylineIds = new Set((paylines || []).map((p) => p.person_id));
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-corrections-panel">
      <div className="px-5 py-3 border-b border-[#eef1f6] flex items-center gap-2">
        <span className="font-display font-semibold text-[15px]">Corrections · {rows.length}</span>
        <span className="font-mono-data text-[12px]" data-testid="payroll-corrections-total">{money(total)}</span>
        {frozen && <span className="inline-flex items-center gap-1 text-[11px] text-[#8a5a1a]" data-testid="payroll-corrections-locked"><Lock size={12} /> Run frozen — read-only</span>}
        {!canEdit && <span className="text-[11px] text-[color:var(--cc-ink-muted)]" data-testid="payroll-corrections-readonly">read-only (payroll editors only)</span>}
        <span className="flex-1" />
        <button type="button" onClick={() => (audit ? setAudit(null) : loadAudit())} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 border rounded" data-testid="payroll-corrections-history-btn"><History size={12} /> History</button>
        <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 border rounded" data-testid="payroll-corrections-export-btn"><Download size={12} /> payout-export.csv</button>
      </div>
      {saved && (
        <div className="px-5 py-2 text-[12px] bg-[#eaf6ee] text-[#1d5b33] flex items-center gap-3" data-testid="payroll-corrections-saved-banner">
          Saved. Re-run Compute to apply.
          {onRecompute && editable && <button type="button" onClick={() => { setSaved(false); onRecompute(); }} className="underline" data-testid="payroll-corrections-recompute-btn">Re-run Compute</button>}
        </div>
      )}
      {err && <div className="px-5 py-2 text-[12px] text-[#7a1c1c] bg-[#fdecec]" data-testid="payroll-corrections-error">{err}</div>}
      <table className="w-full text-[12.5px]">
        <tbody>
          {rows.map((r) => (edit?.id === r.id ? (
            <tr key={r.id} className="border-t border-[#eef1f6] bg-[#f9fafb]" data-testid={`payroll-correction-edit-row-${r.id}`}>
              <td colSpan={6} className="py-2 px-5">
                <div className="flex flex-wrap gap-2 items-end text-[12px]">
                  <CorrectionForm value={edit} onChange={setEdit} roster={roster} testPrefix="payroll-correction-edit" />
                  <button type="button" onClick={saveEdit} disabled={busy} className="px-3 py-1 rounded bg-[#0e1420] text-white" data-testid="payroll-correction-edit-save">Save</button>
                  <button type="button" onClick={() => setEdit(null)} className="px-3 py-1 rounded border" data-testid="payroll-correction-edit-cancel">Cancel</button>
                </div>
              </td>
            </tr>
          ) : (
            <tr key={r.id} className="border-t border-[#eef1f6]" data-testid={`payroll-correction-row-${r.id}`}>
              <td className="py-2 px-5 font-semibold">{r.tech_name_snapshot}</td>
              <td className="py-2 px-3 text-right font-mono-data" data-testid={`payroll-correction-amount-${r.id}`}>{money(r.amount)}</td>
              <td className="py-2 px-3">{r.reason}</td>
              <td className="py-2 px-3 text-[11px] text-[color:var(--cc-ink-muted)]">{r.source_week_monday || ""}</td>
              <td className="py-2 px-3">
                {paylineIds.size > 0 && !paylineIds.has(r.tech_profile_id) && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#8a5a1a]" data-testid={`payroll-correction-unmatched-${r.id}`}>
                    <AlertTriangle size={12} /> No payline for this tech this week — not in any total
                  </span>
                )}
              </td>
              <td className="py-2 px-3 text-right whitespace-nowrap">
                {editable && (
                  <>
                    <button type="button" onClick={() => setEdit({ id: r.id, tech_profile_id: r.tech_profile_id, amount: String(r.amount), reason: r.reason, source_week_monday: r.source_week_monday || "" })}
                            className="mr-2" data-testid={`payroll-correction-edit-${r.id}`}><Pencil size={13} /></button>
                    <button type="button" onClick={() => remove(r.id)} disabled={busy} data-testid={`payroll-correction-delete-${r.id}`}><Trash2 size={13} /></button>
                  </>
                )}
              </td>
            </tr>
          )))}
        </tbody>
      </table>
      {editable && (
        <div className="px-5 py-3 border-t border-[#eef1f6] flex flex-wrap gap-2 items-end text-[12px]" data-testid="payroll-corrections-form">
          <CorrectionForm value={form} onChange={setForm} roster={roster} testPrefix="payroll-correction-add" />
          <button type="button" onClick={add} disabled={busy} className="px-3 py-1 rounded bg-[#0e1420] text-white disabled:opacity-40" data-testid="payroll-correction-add-btn">Add correction</button>
        </div>
      )}
      {audit && (
        <div className="px-5 py-3 border-t border-[#eef1f6] text-[11.5px]" data-testid="payroll-corrections-history">
          {audit.length === 0 && <div>No history.</div>}
          {audit.map((a, i) => (
            <div key={i} className="py-1 border-b border-[#f1f2f5]" data-testid={`payroll-corrections-history-row-${i}`}>
              <span className="font-mono-data">{a.ts}</span> · {a.actor_email} · {a.event_type.replace("payroll.correction.", "")}
              {" · "}{a.before ? `${money(a.before.amount)} "${a.before.reason}"` : "—"} → {a.after ? `${money(a.after.amount)} "${a.after.reason}"` : "deleted"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
